/**
 * The homepage's running totals: "N hours transcribed · N files transcribed · N subtitle files
 * converted · Trusted by N users", from GET /e/totals, which the edge Worker
 * answers from D1 and caches for ten minutes (deploy/cloudflare-worker/totals.js). Modal is never
 * asked.
 *
 * NOTHING IS SERVER-RENDERED BUT THE WORDS. The homepage is cached at the edge for a day, so a
 * number in its HTML would be a day stale; the strip ships `hidden`, with every number 0, and
 * this fills it in the visitor's browser. It stays hidden when the fetch fails or when the answer
 * is not the shape below, and a number is never made up.
 *
 * EACH NUMBER SHOWS ONLY FROM 100 (founder, 2026-10-20). Below that a counter is hidden on its
 * own, because "16 subtitle files converted" works against the page; the strip shows as soon as
 * one of the five reaches it, and each of the others joins the day it crosses.
 *
 * THE LAST TWO ARE NEWER than the Worker that answers without them: an answer with no
 * `files_processed` or `visitors` (a Worker deployed before them, or rolled back) keeps
 * that counter hidden and shows the rest. Present and not a count, it is a bad answer like any.
 *
 * Each number counts up from 0 once, when the strip first scrolls into view. With
 * `prefers-reduced-motion: reduce`, or no IntersectionObserver, the final numbers are written at
 * once. Numbers are formatted for the page's language (`<html lang>`): whole hours from 10 up,
 * one decimal below that.
 */

export const TOTALS_ENDPOINT = '/e/totals';
/** A counter is shown from this value up (every one of the five alike). */
export const SHOW_AT = 100;
/** How long the count-up runs. */
export const COUNT_MS = 1200;

export interface Totals {
  hours_transcribed: number;
  files_transcribed: number;
  files_converted: number;
  /** Null when the answer has no such field yet: the counter stays hidden. */
  files_processed: number | null;
  visitors: number | null;
}

/** The four numbers shown, in the strip's order, by the `data-totals-value` the template gives
 * each. `files_processed` is still parsed but not shown (founder, 2026-10-23: it read like the
 * other two). */
export type TotalKey = 'hours' | 'files' | 'converted' | 'users';
const FIELD: Readonly<Record<TotalKey, keyof Totals>> = {
  hours: 'hours_transcribed',
  files: 'files_transcribed',
  converted: 'files_converted',
  users: 'visitors',
};
const KEYS = Object.keys(FIELD) as TotalKey[];

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;

/** The answer's numbers, or null when it is anything else (the newer two may be missing). */
export function parseTotals(value: unknown): Totals | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const body = value as Record<string, unknown>;
  const hours = body['hours_transcribed'];
  const files = body['files_transcribed'];
  const converted = body['files_converted'];
  if (!isCount(hours) || !isCount(files) || !isCount(converted)) return null;
  const processed = body['files_processed'];
  const users = body['visitors'];
  if (processed !== undefined && !isCount(processed)) return null;
  if (users !== undefined && !isCount(users)) return null;
  return {
    hours_transcribed: hours,
    files_transcribed: files,
    files_converted: converted,
    files_processed: processed ?? null,
    visitors: users ?? null,
  };
}

/** A counter's number, or null when the answer did not carry it. */
const countOf = (totals: Totals, key: TotalKey): number | null => totals[FIELD[key]];

/** Below this, hours show one decimal; 9.96 would round to "10.0", so it is "10" instead. */
const DECIMAL_BELOW = 9.95;

const decimalsFor = (key: TotalKey, final: number): number =>
  key === 'hours' && final < DECIMAL_BELOW ? 1 : 0;

/**
 * One number as the strip shows it: whole hours from 10 up, one decimal below; whole files.
 * `final` picks the decimals, so a count on its way to 1,234 hours never shows "5.3".
 */
export function formatTotal(key: TotalKey, value: number, lang?: string, final = value): string {
  const decimals = decimalsFor(key, final);
  try {
    return new Intl.NumberFormat(lang || undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch {
    return value.toFixed(decimals);
  }
}

/** The counters that have reached SHOW_AT, in the strip's order. */
export function shownKeys(totals: Totals): TotalKey[] {
  return KEYS.filter((key) => (countOf(totals, key) ?? 0) >= SHOW_AT);
}

/** True when at least one counter has reached SHOW_AT. */
export function worthShowing(totals: Totals): boolean {
  return shownKeys(totals).length > 0;
}

/** GET /e/totals, or null on any failure. Never throws. */
export async function fetchTotals(fetchFn: typeof fetch = fetch): Promise<Totals | null> {
  try {
    const response = await fetchFn(TOTALS_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return parseTotals(await response.json());
  } catch {
    return null;
  }
}

/** What `mountTotals` takes from the browser, so a test can hand it its own. */
export interface TotalsEnv {
  fetch?: typeof fetch;
  lang?: string;
  reducedMotion?: boolean;
  /** Undefined means the browser's; null means none. */
  IntersectionObserver?: typeof IntersectionObserver | null;
  requestAnimationFrame?: (callback: (now: number) => void) => unknown;
  now?: () => number;
}

/** The ease the count slows into: fast first, settling on the number. */
const easeOut = (t: number): number => 1 - (1 - t) ** 3;

/**
 * Fill the strip and reveal it, or leave it hidden. Resolves once the numbers are written (the
 * final ones, or 0s waiting for the strip to scroll into view); true when the strip is shown.
 */
export async function mountTotals(strip: HTMLElement, env: TotalsEnv = {}): Promise<boolean> {
  try {
    const slots = new Map<TotalKey, HTMLElement>();
    for (const key of KEYS) {
      const slot = strip.querySelector<HTMLElement>(`[data-totals-value="${key}"]`);
      if (!slot) return false;
      slots.set(key, slot);
    }
    const totals = await fetchTotals(env.fetch ?? globalThis.fetch?.bind(globalThis));
    if (totals === null || !worthShowing(totals)) {
      strip.hidden = true;
      return false;
    }
    // Each counter's own line shows only once it has reached SHOW_AT.
    const shown = new Set(shownKeys(totals));
    for (const key of KEYS) {
      const item = strip.querySelector<HTMLElement>(`[data-totals-item="${key}"]`);
      if (item) item.hidden = !shown.has(key);
      if (!shown.has(key)) slots.delete(key);
    }

    const lang = env.lang ?? globalThis.document?.documentElement?.lang ?? undefined;
    const write = (fraction: number): void => {
      for (const [key, slot] of slots) {
        const final = countOf(totals, key) ?? 0;
        slot.textContent = formatTotal(key, fraction >= 1 ? final : final * fraction, lang, final);
      }
    };

    // The final width is reserved before anything moves, so a count that adds digits never
    // re-wraps the strip under the visitor's eyes.
    for (const [key, slot] of slots) {
      slot.style.minWidth = `${formatTotal(key, countOf(totals, key) ?? 0, lang).length}ch`;
    }

    const reduced =
      env.reducedMotion ??
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ??
      false;
    const Observer =
      env.IntersectionObserver === undefined
        ? globalThis.IntersectionObserver
        : env.IntersectionObserver;
    const raf = env.requestAnimationFrame ?? globalThis.requestAnimationFrame?.bind(globalThis);
    const now = env.now ?? (() => globalThis.performance?.now() ?? Date.now());

    if (reduced || !Observer || !raf) {
      write(1);
      strip.hidden = false;
      return true;
    }

    write(0);
    strip.hidden = false;
    const observer = new Observer((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      const start = now();
      const step = (): void => {
        const t = Math.min(1, (now() - start) / COUNT_MS);
        write(t >= 1 ? 1 : easeOut(t));
        if (t < 1) raf(step);
      };
      raf(step);
    });
    observer.observe(strip);
    return true;
  } catch {
    strip.hidden = true;
    return false;
  }
}
